import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { AlertCircle, CheckCircle2, ArrowLeftRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from '@/components/ui/progress';

interface ReassignmentResult {
  lead_id: string;
  partner_id: string | null;
  partner_name?: string;
  match_method: string;
  email_sent: boolean;
}

export function LeadReassignment() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ReassignmentResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const { toast } = useToast();

  const handleReassignLeads = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    setResults([]);
    
    try {
      const { data, error } = await supabase.functions.invoke('reassign-leads');
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.success) {
        setResults(data.results);
        setSuccess(true);
        toast({
          title: "Επιτυχής επανεκχώρηση",
          description: `Επεξεργάστηκαν ${data.processed} ενδιαφερόμενοι.`,
          variant: "default",
        });
      } else {
        throw new Error("Failed to reassign leads");
      }
    } catch (err) {
      console.error("Error reassigning leads:", err);
      setError(err.message || "Προέκυψε σφάλμα κατά την επανεκχώρηση");
      toast({
        title: "Σφάλμα επανεκχώρησης",
        description: err.message || "Προέκυψε σφάλμα κατά την επανεκχώρηση των ενδιαφερόμενων",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Count the number of leads that were actually reassigned
  const reassignedCount = results.filter(r => r.match_method !== "no_change_needed").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRight className="h-5 w-5" />
          Επανεκχώρηση Ενδιαφερόμενων
        </CardTitle>
        <CardDescription>
          Επανεξέταση και επανεκχώρηση ενδιαφερόμενων στους κατάλληλους συνεργάτες βάσει τοποθεσίας και υπηρεσίας.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Σφάλμα</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && results.length > 0 && (
          <Alert variant={reassignedCount > 0 ? "default" : "default"} className="mb-4">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Ολοκληρώθηκε</AlertTitle>
            <AlertDescription>
              {reassignedCount > 0 
                ? `Επανεκχωρήθηκαν ${reassignedCount} από ${results.length} ενδιαφερόμενους.` 
                : "Δεν χρειάστηκε να γίνει επανεκχώρηση κανενός ενδιαφερόμενου."}
            </AlertDescription>
          </Alert>
        )}
        
        {loading && (
          <div className="space-y-2 mb-4">
            <p className="text-sm text-muted-foreground">Επεξεργασία ενδιαφερόμενων...</p>
            <Progress value={100} className="w-full animate-pulse" />
          </div>
        )}
        
        {results.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Αποτελέσματα:</h4>
            <div className="max-h-60 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="py-2 px-3 text-left">ID Ενδιαφερόμενου</th>
                    <th className="py-2 px-3 text-left">Συνεργάτης</th>
                    <th className="py-2 px-3 text-left">Τρόπος αντιστοίχισης</th>
                    <th className="py-2 px-3 text-left">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {results.map((result) => (
                    <tr key={result.lead_id} className={result.match_method !== 'no_change_needed' ? 'bg-muted/40' : ''}>
                      <td className="py-2 px-3">{result.lead_id.substring(0, 8)}...</td>
                      <td className="py-2 px-3">{result.partner_name || result.partner_id || 'N/A'}</td>
                      <td className="py-2 px-3">
                        {result.match_method === 'coordinates_match' && 'Συντεταγμένες'}
                        {result.match_method === 'location_name_match' && 'Όνομα τοποθεσίας'}
                        {result.match_method === 'address_text_match' && 'Κείμενο διεύθυνσης'}
                        {result.match_method === 'no_change_needed' && 'Καμία αλλαγή'}
                      </td>
                      <td className="py-2 px-3">
                        {result.email_sent 
                          ? <span className="text-green-600">Εστάλη</span> 
                          : <span className="text-gray-500">Όχι</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleReassignLeads} 
          disabled={loading}
          className="w-full"
        >
          {loading ? 'Επεξεργασία...' : 'Έναρξη επανεκχώρησης'}
        </Button>
      </CardFooter>
    </Card>
  );
}
